// Mutually-exclusive right-hand panels (Thread XOR Profile) — a NEUTRAL coordinator
// so app.svelte.ts and profile-panel.svelte.ts do NOT import each other. A direct
// cross-import between them formed a circular dependency that broke the open
// handlers at runtime (opening either panel threw / no-op'd). Instead, each store
// registers its own close fn here at module load, and opening one panel calls the
// other's registered closer. Plain imperative callbacks (no reactive reads), so the
// two can never form a reactive close-loop; the just-opened panel always wins.
let closeThreadFn: (() => void) | null = null;
let closeProfileFn: (() => void) | null = null;

// Each panel's live open state, mirrored here via a $effect in the panel (see
// noteThreadOpen / noteProfileOpen). Used ONLY to decide whether a close() below is
// a genuine SWITCH (the other panel is currently open) vs a plain close — which is
// what `replacing` gates the enter/exit transitions on (see isReplacing).
let threadOpen = false;
let profileOpen = false;

// TRUE only for the single frame during which one panel is being replaced by the
// other (a thread↔profile switch). Both panels read this via isReplacing() to run
// their fly transition with duration 0 (instant swap) instead of a double
// fly-out + fly-in animation, which looks wrong. A FRESH open (nothing was showing)
// leaves this false, so the incoming panel gets its normal 200ms fly-in.
let replacing = false;

/** Called once at app.svelte.ts module load with its `closeThread`. */
export function registerThreadCloser(fn: () => void): void {
  closeThreadFn = fn;
}

/** Called once at profile-panel.svelte.ts module load with its `close`. */
export function registerProfileCloser(fn: () => void): void {
  closeProfileFn = fn;
}

/** ThreadPanel mirrors its open state here (via a $effect) so a switch is detectable. */
export function noteThreadOpen(v: boolean): void {
  threadOpen = v;
}

/** ProfilePanel mirrors its open state here (via a $effect) so a switch is detectable. */
export function noteProfileOpen(v: boolean): void {
  profileOpen = v;
}

/** True during a thread↔profile SWITCH — both panels skip their fly transition then. */
export function isReplacing(): boolean {
  return replacing;
}

// Reset `replacing` AFTER Svelte's flush so the mount (incoming enter) and unmount
// (outgoing exit) transitions are BOTH created while `replacing` is still true —
// giving them duration 0 — then clear it on the next frame. rAF runs after the flush;
// queueMicrotask would fire before the transitions are constructed and re-arm the
// animation. Guarded for non-browser (SSR/tests) where rAF is undefined.
function resetReplacingAfterFlush(): void {
  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => {
      replacing = false;
    });
  } else {
    replacing = false;
  }
}

/** Close any open thread (invoked when a profile opens). No-op until registered. */
export function closeOpenThread(): void {
  // Only a genuine switch (a thread WAS open) suppresses the transition; a profile
  // opening from nothing must keep its fly-in.
  if (threadOpen) replacing = true;
  closeThreadFn?.();
  if (threadOpen) resetReplacingAfterFlush();
}

/** Close any open profile (invoked when a thread opens). No-op until registered. */
export function closeOpenProfile(): void {
  if (profileOpen) replacing = true;
  closeProfileFn?.();
  if (profileOpen) resetReplacingAfterFlush();
}
